import type { EntradasDoMes, RealizadoDoMes } from "@base/contracts";
import type { MetaDoMes } from "./calculo";

export type Formato = "moeda" | "quantidade" | "porcentagem" | "multiplicador";
export type Secao = "midia" | "funil" | "financeiro";

/**
 * Uma linha da grade: a meta (digitada ou calculada), o realizado e quanto foi
 * atingido. Custos sao "menor-melhor": gastar menos que a meta e bom.
 */
export type LinhaDoFunil = {
  readonly id: string;
  readonly rotulo: string;
  readonly descricao: string;
  readonly secao: Secao;
  readonly formato: Formato;
  /** Quando existe, a meta desta linha e digitada (celula editavel). */
  readonly entrada?: keyof EntradasDoMes;
  readonly sentido: "maior-melhor" | "menor-melhor";
  readonly meta: (meta: MetaDoMes) => number | null;
  readonly realizado: (realizado: RealizadoDoMes) => number | null;
};

const dividir = (a: number | null, b: number | null): number | null =>
  a === null || b === null || b <= 0 ? null : a / b;

export const ROTULO_DA_SECAO: Readonly<Record<Secao, string>> = {
  midia: "Mídia paga",
  funil: "Funil de vendas",
  financeiro: "Financeiro",
};

export const LINHAS: readonly LinhaDoFunil[] = [
  {
    id: "orcamento",
    rotulo: "Orçamento de mídia",
    descricao: "Verba do mês em anúncios",
    secao: "midia",
    formato: "moeda",
    entrada: "orcamentoCentavos",
    sentido: "menor-melhor",
    meta: (m) => m.orcamentoCentavos,
    realizado: (r) => r.investimentoCentavos,
  },
  {
    id: "cpl",
    rotulo: "Custo por lead",
    descricao: "Quanto custa cada lead",
    secao: "midia",
    formato: "moeda",
    entrada: "cplCentavos",
    sentido: "menor-melhor",
    meta: (m) => m.cplCentavos,
    realizado: (r) => dividir(r.investimentoCentavos, r.leadsGerenciador),
  },
  {
    id: "leads-gerenciador",
    rotulo: "Leads no gerenciador",
    descricao: "Orçamento ÷ custo por lead",
    secao: "funil",
    formato: "quantidade",
    sentido: "maior-melhor",
    meta: (m) => m.leads,
    realizado: (r) => r.leadsGerenciador,
  },
  {
    id: "leads-crm",
    rotulo: "Leads no CRM",
    descricao: "Mesma meta do gerenciador",
    secao: "funil",
    formato: "quantidade",
    sentido: "maior-melhor",
    meta: (m) => m.leads,
    realizado: (r) => r.leadsCrm,
  },
  {
    id: "taxa-mql",
    rotulo: "% Lead → MQL",
    descricao: "Leads qualificados",
    secao: "funil",
    formato: "porcentagem",
    entrada: "taxaMql",
    sentido: "maior-melhor",
    meta: (m) => m.taxaMql,
    realizado: (r) => dividir(r.mql, r.leadsCrm),
  },
  {
    id: "mql",
    rotulo: "MQL",
    descricao: "Leads × % MQL",
    secao: "funil",
    formato: "quantidade",
    sentido: "maior-melhor",
    meta: (m) => m.mql,
    realizado: (r) => r.mql,
  },
  {
    id: "taxa-agendamento",
    rotulo: "% MQL → Agendamento",
    descricao: "Qualificados que agendam",
    secao: "funil",
    formato: "porcentagem",
    entrada: "taxaAgendamento",
    sentido: "maior-melhor",
    meta: (m) => m.taxaAgendamento,
    realizado: (r) => dividir(r.agendamentos, r.mql),
  },
  {
    id: "agendamentos",
    rotulo: "Agendamentos",
    descricao: "MQL × % agendamento",
    secao: "funil",
    formato: "quantidade",
    sentido: "maior-melhor",
    meta: (m) => m.agendamentos,
    realizado: (r) => r.agendamentos,
  },
  {
    id: "taxa-consulta",
    rotulo: "% Agendamento → Consulta",
    descricao: "Quem comparece",
    secao: "funil",
    formato: "porcentagem",
    entrada: "taxaConsulta",
    sentido: "maior-melhor",
    meta: (m) => m.taxaConsulta,
    realizado: (r) => dividir(r.consultas, r.agendamentos),
  },
  {
    id: "consultas",
    rotulo: "Consultas realizadas",
    descricao: "Agendamentos × % consulta",
    secao: "funil",
    formato: "quantidade",
    sentido: "maior-melhor",
    meta: (m) => m.consultas,
    realizado: (r) => r.consultas,
  },
  {
    id: "taxa-venda",
    rotulo: "% Consulta → Venda",
    descricao: "Quem fecha o tratamento",
    secao: "funil",
    formato: "porcentagem",
    entrada: "taxaVenda",
    sentido: "maior-melhor",
    meta: (m) => m.taxaVenda,
    realizado: (r) => dividir(r.vendas, r.consultas),
  },
  {
    id: "vendas",
    rotulo: "Vendas",
    descricao: "Consultas × % venda",
    secao: "funil",
    formato: "quantidade",
    sentido: "maior-melhor",
    meta: (m) => m.vendas,
    realizado: (r) => r.vendas,
  },
  {
    id: "ticket",
    rotulo: "Ticket médio",
    descricao: "Valor médio por venda",
    secao: "financeiro",
    formato: "moeda",
    entrada: "ticketMedioCentavos",
    sentido: "maior-melhor",
    meta: (m) => m.ticketMedioCentavos,
    realizado: (r) => dividir(r.faturamentoCentavos, r.vendas),
  },
  {
    id: "faturamento-marketing",
    rotulo: "Faturamento marketing",
    descricao: "Vendas × ticket · Origem Mídia Paga",
    secao: "financeiro",
    formato: "moeda",
    sentido: "maior-melhor",
    meta: (m) => m.faturamentoMarketingCentavos,
    realizado: (r) => r.faturamentoMarketingCentavos,
  },
  {
    id: "faturamento-clinica",
    rotulo: "Faturamento clínica",
    descricao: "Indicação, orgânico e demais origens",
    secao: "financeiro",
    formato: "moeda",
    entrada: "faturamentoClinicaCentavos",
    sentido: "maior-melhor",
    meta: (m) => m.faturamentoClinicaCentavos,
    realizado: (r) =>
      r.faturamentoMarketingCentavos === null ? null : r.faturamentoCentavos - r.faturamentoMarketingCentavos,
  },
  {
    id: "faturamento-total",
    rotulo: "Faturamento total",
    descricao: "Marketing + clínica",
    secao: "financeiro",
    formato: "moeda",
    sentido: "maior-melhor",
    meta: (m) => m.faturamentoTotalCentavos,
    realizado: (r) => r.faturamentoCentavos,
  },
  {
    id: "roas",
    rotulo: "ROAS",
    descricao: "Faturamento marketing ÷ orçamento",
    secao: "financeiro",
    formato: "multiplicador",
    sentido: "maior-melhor",
    meta: (m) => m.roas,
    realizado: (r) => dividir(r.faturamentoMarketingCentavos, r.investimentoCentavos),
  },
  {
    id: "custo-consulta",
    rotulo: "Custo por consulta",
    descricao: "Orçamento ÷ consultas",
    secao: "financeiro",
    formato: "moeda",
    sentido: "menor-melhor",
    meta: (m) => m.custoPorConsultaCentavos,
    realizado: (r) => dividir(r.investimentoCentavos, r.consultas),
  },
  {
    id: "cpa",
    rotulo: "CPA",
    descricao: "Orçamento ÷ vendas",
    secao: "financeiro",
    formato: "moeda",
    sentido: "menor-melhor",
    meta: (m) => m.cpaCentavos,
    realizado: (r) => dividir(r.investimentoCentavos, r.vendas),
  },
];

/** Soma dos meses para a coluna Total; o realizado da linha e recalculado sobre ela. */
export function somarRealizado(meses: readonly RealizadoDoMes[]): RealizadoDoMes {
  const somar = (campo: (mes: RealizadoDoMes) => number) => meses.reduce((total, mes) => total + campo(mes), 0);
  const somarOuNulo = (campo: (mes: RealizadoDoMes) => number | null) =>
    meses.every((mes) => campo(mes) === null) ? null : meses.reduce((total, mes) => total + (campo(mes) ?? 0), 0);
  return {
    mes: 0,
    leadsGerenciador: somarOuNulo((mes) => mes.leadsGerenciador),
    investimentoCentavos: somarOuNulo((mes) => mes.investimentoCentavos),
    leadsCrm: somar((mes) => mes.leadsCrm),
    mql: somar((mes) => mes.mql),
    agendamentos: somar((mes) => mes.agendamentos),
    consultas: somar((mes) => mes.consultas),
    vendas: somar((mes) => mes.vendas),
    faturamentoCentavos: somar((mes) => mes.faturamentoCentavos),
    faturamentoMarketingCentavos: somarOuNulo((mes) => mes.faturamentoMarketingCentavos),
  };
}

/** Realizado ÷ meta. `null` sem meta ou sem realizado. */
export function atingido(meta: number | null, realizado: number | null): number | null {
  return meta === null || realizado === null || meta <= 0 ? null : realizado / meta;
}
